import { createFrontendModule, ApiBlueprint } from '@backstage/frontend-plugin-api';
import { costInsightsAwsPlugin } from '@aws/cost-insights-plugin-for-backstage';
import type { AnyApiFactory } from '@backstage/core-plugin-api';

/**
 * AWS Cost Insights (Wave 4 — see docs/plugins/README.md +
 * docs/operations.md → "AWS Labs plugin suite").
 *
 * `@aws/cost-insights-plugin-for-backstage` is NOT itself a page — reading
 * its compiled output (dist/plugin.esm.js) shows it only registers a
 * `costInsightsApiRef` factory (a `CostExplorerClient`, calling AWS Cost
 * Explorer instead of the default backend-proxy implementation), meant to
 * back Backstage's own `@backstage-community/plugin-cost-insights` page
 * (installed separately, native `/alpha` export, registered directly in
 * App.tsx's features array).
 *
 * `CostExplorerClient` isn't a public export of the AWS package, so rather
 * than reach into private internals this pulls the already-built
 * `AnyApiFactory` back out via the plugin's own public `getApis()` method
 * (part of the stable `BackstagePlugin` interface) and re-registers it as
 * the new frontend system's implementation of the SAME `costInsightsApiRef`
 * the community plugin's own default extension provides — this module
 * overrides that one extension (same pluginId + name) rather than adding a
 * competing API.
 *
 * Config: `costInsights.engineerCost` (required by the community plugin)
 * lives in app-config.yaml. Cost Explorer's `ce:GetCostAndUsage` API costs
 * $0.01/request — DevOps must grant that IAM action before this returns
 * real data (see docs/operations.md).
 *
 * Registered under a distinct extension name ('aws-cost-explorer', not
 * 'cost-insights') rather than attempting an in-place override of the
 * community plugin's own built-in `api:cost-insights` extension — getting
 * an override id wrong fails LOUD (Backstage throws on two live factories
 * for the same ApiRef), so app-config.yaml's `app.extensions` explicitly
 * disables the built-in one instead of relying on name-matching here.
 */
const [awsCostExplorerApiFactory] = Array.from(
  costInsightsAwsPlugin.getApis(),
) as [AnyApiFactory];

const awsCostInsightsApi = ApiBlueprint.make({
  name: 'aws-cost-explorer',
  // ApiBlueprint requires the factory wrapped in this callback form (same
  // shape `@backstage/core-compat-api`'s own `convertLegacyPlugin` uses
  // internally to bridge a legacy plugin's `getApis()` factories) rather
  // than passed directly.
  params: defineParams => defineParams(awsCostExplorerApiFactory),
});

export const awsCostInsightsModule = createFrontendModule({
  pluginId: 'cost-insights',
  extensions: [awsCostInsightsApi],
});
